import { ExternalLinkIcon, LogOutIcon } from 'lucide-react'
import { tempo } from 'viem/chains'

import { CopyLinkButton } from '~/components/CopyLinkButton'
import { Button } from '~/components/component-library'
import { ApproveTransactionButton } from '~/components/smart-accounts/ApproveTransactionButton'
import { usePrivyWalletBridge } from '~/components/smart-accounts/usePrivyWalletBridge'
import { useHasAccessOverride } from '~/components/txn-display/action/useHasAccessOverride'
import { useBridgeReceiptUrl } from '~/components/txn-display/state-change/hooks/useBridgeReceiptUrl'
import {
  CONFIRMED_TRANSACTION_STATUSES,
  QUEUE_TRANSACTION_STATUSES,
} from '~/constants/smart_accounts'
import { useTransactionSmartAccount } from '~/hooks/useSmartAccounts'
import { BackendTransaction } from '~/types'
import {
  getExplorerTransactionLinkChainId,
  getExplorerUserOpLink,
} from '~/utils/addresses'
import { captureException } from '~/utils/errors'
import { getTransactionUrl } from '~/utils/smartAccounts/transactions'

export const TransactionActions = ({
  transaction,
  onClose,
  onConfirmation,
  onSubmitError,
  isSimulationRevert,
  simulationRevertReason,
}: {
  transaction: BackendTransaction
  onClose?: () => void
  onConfirmation?: () => void
  onSubmitError?: (e: unknown) => void
  isSimulationRevert?: boolean
  simulationRevertReason?: string
}) => {
  const { smartAccount } = useTransactionSmartAccount(transaction)
  const privyBridge = usePrivyWalletBridge({
    eoaSigners: smartAccount?.eoaSigners,
  })
  const { hasPrivyUser, disconnectPrivyAndEOAs } = privyBridge
  const hasAccessOverride = useHasAccessOverride(transaction)
  const explorerLink = getExplorerUserOpLink(
    transaction.userOpHash,
    transaction.chainId,
  )
  const shouldShowExplorerLink =
    !transaction.isMerkleTransaction &&
    CONFIRMED_TRANSACTION_STATUSES.includes(transaction.status) &&
    !!explorerLink
  const shouldShowApprovalButton =
    !shouldShowExplorerLink &&
    QUEUE_TRANSACTION_STATUSES.includes(transaction.status)
  const bridgeReceiptUrl = useBridgeReceiptUrl(transaction)
  const copyLinkButton = (
    <div>
      <CopyLinkButton
        trackingId={`transaction.${transaction.id}`}
        link={getTransactionUrl(transaction)}
      />
    </div>
  )

  if (shouldShowExplorerLink) {
    let explorerUrl = getExplorerUserOpLink(
      transaction!.userOpHash,
      transaction!.chainId,
    )

    // Tempo's explorer doesn't support userOp deep links, so fall back to the transaction hash link
    if (transaction.chainId === tempo.id) {
      explorerUrl = getExplorerTransactionLinkChainId(
        transaction.transactionHash,
        transaction.chainId,
      )
    }

    if (bridgeReceiptUrl) {
      explorerUrl = bridgeReceiptUrl
    }

    return (
      <TransactionActionsInner>
        {hasPrivyUser && (
          <Button
            variant="secondary"
            prefix={<LogOutIcon />}
            onClick={() => {
              void disconnectPrivyAndEOAs().catch((e) => captureException(e))
            }}
          >
            Log out
          </Button>
        )}
        {copyLinkButton}
        <Button
          variant="secondary"
          prefix={<ExternalLinkIcon />}
          href={{
            path: explorerUrl!,
            target: 'tab',
          }}
        >
          {bridgeReceiptUrl ? 'Receipt' : 'Open explorer'}
        </Button>
      </TransactionActionsInner>
    )
  } else if (shouldShowApprovalButton) {
    return (
      <TransactionActionsInner>
        {copyLinkButton}
        <ApproveTransactionButton
          transaction={transaction}
          privyBridge={privyBridge}
          shortcut={true}
          hasAccessOverride={hasAccessOverride}
          onClose={onClose}
          onConfirmation={onConfirmation}
          onSubmitError={onSubmitError}
          isDisabledOverride={isSimulationRevert}
          disabledReasonOverride={
            isSimulationRevert
              ? simulationRevertReason || 'Simulation reverted'
              : undefined
          }
        />
      </TransactionActionsInner>
    )
  } else {
    return (
      <TransactionActionsInner>
        {hasPrivyUser && (
          <Button
            variant="secondary"
            prefix={<LogOutIcon />}
            onClick={() => {
              void disconnectPrivyAndEOAs().catch((e) => captureException(e))
            }}
          >
            Log out
          </Button>
        )}
        {copyLinkButton}
      </TransactionActionsInner>
    )
  }
}

const TransactionActionsInner = ({
  children,
}: {
  children?: React.ReactNode
}) => <div className="flex w-full justify-end gap-4">{children}</div>
